/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import { entities } from '../../../constants/entities'
import { formatDateFront } from '../../../utils/formatDate'
import useAction from '../../../hooks/useAction'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import useForm from '../../../hooks/useForm'
import { setDefaultFieldValues } from '../../../utils/objectUtils'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import Form from '../../../components/forms/Form'
import { filterFields } from './filters'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import Button from '../../../components/Button'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useSelectOptions from '../../../hooks/useSelectOptions'
import { EquipmentEnum } from '../../../enums/equipment-enum'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { useOrgan } from '../../../hooks/useOrgan'
import { useReport } from '../../../context/ReportContext'

const EquipmentByContract = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { selectedOrgan } = useOrgan()

  const { setExportId, isExporting } = useReport()

  const [filtersSection] = filterFields
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([filtersSection]),
    [filtersSection]
  )

  // Se ao menos um tiver preenchido marcar como true
  const [hasFilled, setHasFilled] = useState(false)

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      status_id: formData.filter.with_reserved?.value
        ? [EquipmentEnum.RENTED, EquipmentEnum.RESERVED]
        : EquipmentEnum.RENTED, // Apenas equipamentos alugados ou reservados
      'lastRelevantMovementItem.movement.order.contract_id':
        formData.filter.contract_id?.value, // Somente equipamentos alugados para o contrato selecionado pelo usuário
      'lastRelevantMovementItem.movement.organization_id':
        selectedOrgan?.id ?? null,
      deleted_at: false,
    },
    'equipmentsByOrganizational'
  )

  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  // Cliente contratante
  const customerSelectOptions = useRemoteSelectOptions({
    queryKey: ['customers'],
    entityPath: entities.customers.contractants.get,
    baseServiceGet,
    fieldId: 'filter.customer_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
  })

  const withReservedSelectOptiosn = useSelectOptions({
    fieldId: 'filter.with_reserved',
    formData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
    rawOptions: [
      { value: true, label: 'Sim' },
      { value: false, label: 'Nao' },
    ],
  })

  useEffect(() => {
    // Por padrão é não
    setFormData((prev) => ({
      filter: {
        ...prev.filter,
        with_reserved: { value: false, label: 'Nao' },
      },
    }))
  }, [])

  const customerSelectedRef = useRef(customerSelectOptions.selectedValue?.value)

  // Contrato do cliente contratante
  const contractSelectOptions = useRemoteSelectOptions({
    queryKey: ['contractsByCustomer'],
    entityPath: entities.contracts.get,
    baseServiceGet,
    extraFilters: { customer_id: customerSelectOptions.selectedValue?.value },
    defaultSort: { column: 'id', order: 'desc' },
    enabled: !!customerSelectOptions.selectedValue?.value,
    fieldId: 'filter.contract_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchWhen: () => {
      // Mudar quando o cliente selecionado mudar
      if (
        customerSelectedRef.current !==
        customerSelectOptions.selectedValue?.value
      ) {
        customerSelectedRef.current = customerSelectOptions.selectedValue?.value
        contractSelectOptions.reset()
        return true
      }

      return false
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
  })

  useEffect(() => {
    const hasFilledValidate = contractSelectOptions.selectedValue?.value
    setHasFilled(hasFilledValidate)
  }, [formData])

  const selects = {
    'filter.customer_id': customerSelectOptions,
    'filter.contract_id': contractSelectOptions,
    'filter.with_reserved': withReservedSelectOptiosn,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const {
    data: equipmentsByOrganizationalData,
    isError,
    error,
    isLoading,
    isFetching,
  } = useQueryWithLoader({
    queryKey: ['equipmentsByOrganizational', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: {
          column: 'number',
          order: 'desc',
        },
      }
      const response = await baseServiceGet(
        entities.equipments.allCleaned.get,
        params
      )
      return response.result
    },
    staleTime: 0,
    enabled: !!hasFilled,
    select: (data) => {
      return {
        equipmentsByOrganizational: data?.data?.map((equipment) => ({
          id: equipment.id,
          equipment: `${equipment.number} - ${equipment.name}`,
          situation: equipment.status?.name ?? '-',
          movement_date:
            formatDateFront({
              rawDate: equipment.last_relevant_movement_item?.movement
                ?.movement_date
                ? equipment.last_relevant_movement_item?.movement?.movement_date
                : equipment.last_relevant_service_order_item?.order?.created_at,
            }) ?? '-',
        })),
        totalPages: data.last_page,
      }
    },
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (isLoading || isFetching) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isLoading, isFetching])

  const { equipmentsByOrganizational = [], totalPages = 1 } =
    equipmentsByOrganizationalData || {}

  // Limpa o data se nenhum filtro tiver selecionado
  useEffect(() => {
    if (!hasFilled) {
      queryClient.setQueryData(
        ['equipmentsByOrganizational', filters, pagination],
        {
          equipmentsByOrganizational: [],
          totalPages: 1,
        }
      )
    }
  }, [hasFilled])

  const columns = [
    { key: 'equipment', label: 'Equipamento', width: '60%' },
    { key: 'situation', label: 'Situação' },
    { key: 'movement_date', label: 'D. Movimento' },
  ]

  useEffect(() => {
    if (isError) {
      showNotification(
        'error',
        `Erro ao carregar faturamentos: ${
          error.response?.data?.message || error.message
        }`
      )
    }
  }, [isError, error, showNotification])

  const exportReport = useMutation({
    mutationFn: async (type) => {
      showLoader()
      // extrai os valores
      const {
        'lastRelevantMovementItem.movement.order.contract_id': lastContractId,
        'lastRelevantMovementItem.movement.organization_id': organizationId,
        ...restFilters
      } = filters

      const params = {
        filters: {
          ...restFilters,
          contract_id: lastContractId,
          organization_id: organizationId,
        },
        sort: {
          column: 'number',
          order: 'desc',
        },
      }
      const response = await baseServiceGet(
        entities.reports.equipmentByContract.get,
        params,
        { type, withReserved: formData.filter.with_reserved.value }
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification(
            'info',
            'Processamento do relatório iniciado em segundo plano.'
          )
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      showNotification('error', 'Erro ao iniciar a geração do relatório.')
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const {
    openModalConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    action,
  } = useAction(navigate)

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Equipamentos por contrato" showBackButton={false} />
      <Alert
        severity="info"
        sx={{
          mb: 2,
        }}>
        <strong>Atenção:</strong> Esse relatório busca todos os equipamentos do
        cliente contratante, incluindo os equipamentos reservados ou não.
      </Alert>
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={() => {}}
          textSubmit="Filtrar"
          textLoadingSubmit="Filtrando..."
          showButtons={false}
          disabledSubmitButton={true}>
          {() => (
            <>
              <div key={filtersSection.section}>
                <SectionHeader section={filtersSection.section} />

                <SimpleBody
                  fields={filtersSection.fields}
                  formErrors={{}}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  my: 4,
                  width: '100%',
                }}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                  text="Exportar em PDF"
                  disabled={
                    !hasFilled ||
                    equipmentsByOrganizational?.length === 0 ||
                    isExporting
                  }
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('pdf')
                    } catch (error) {
                      console.error('Erro ao exportar PDF:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                  text="Exportar em EXCEL"
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('excel')
                    } catch (error) {
                      console.error('Erro ao exportar EXCEL:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                  disabled={
                    !hasFilled ||
                    equipmentsByOrganizational?.length === 0 ||
                    isExporting
                  }
                />
              </Box>
            </>
          )}
        </Form>

        <TableWithInactiveFilter
          data={equipmentsByOrganizational}
          columns={columns}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          applyUrlFilters={applyUrlFilters}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          type="default"
          showFilterInactive={false}
          isLoading={isLoading}
          isFetching={isFetching || exportReport.isPending}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={
            `N° do Movimento (${selectedItem?.number}) - Cliente: ${selectedItem?.customer}` ||
            ''
          }
          text={action?.text}
        />
      </div>
    </MainLayout>
  )
}

export default EquipmentByContract
